import TextEdit from '../../../molecules/TextEdit';
import SelectTextField from '../../../molecules/SelectTextField';
import CurrencyInput from '../../../molecules/CurrencyInput';
import {
    INVENTORY_TYPE_OPTIONS,
    LEGAL_REGIME_OPTIONS,
    MAX_OWNERS,
} from '../../../../types/Inventory';
import type {
    InventoryOwner,
    InventoryType as InventoryTypeValue,
    LegalRegime as LegalRegimeValue,
} from '../../../../types/Inventory';
import type { ErrorField } from '../../../../types/ErrorField';
import { getErrorMessage } from '../../../../utils/formErrorUtils';

const UF_OPTIONS = ['AC','AL','AM','AP','BA','CE','DF','ES','GO','MA','MG','MS','MT','PA','PB','PE','PI','PR','RJ','RN','RO','RR','RS','SC','SE','SP','TO']
    .map(uf => ({ value: uf, label: uf }));

interface InventoryFormProps {
    errorFields: ErrorField[];
    name: string; setName: (v: string) => void;
    description: string; setDescription: (v: string) => void;
    inventoryType: InventoryTypeValue | ''; setInventoryType: (v: InventoryTypeValue | '') => void;
    legalRegime: LegalRegimeValue | ''; setLegalRegime: (v: LegalRegimeValue | '') => void;
    quantityDescription: string; setQuantityDescription: (v: string) => void;
    custodyAddress: string; setCustodyAddress: (v: string) => void;
    custodyCity: string; setCustodyCity: (v: string) => void;
    custodyState: string; setCustodyState: (v: string) => void;
    registrationNumber: string; setRegistrationNumber: (v: string) => void;
    registryOffice: string; setRegistryOffice: (v: string) => void;
    registryState: string; setRegistryState: (v: string) => void;
    trusteeName: string; setTrusteeName: (v: string) => void;
    trusteeDocument: string; setTrusteeDocument: (v: string) => void;
    certificateId: string; setCertificateId: (v: string) => void;
    certificateDate: string; setCertificateDate: (v: string) => void;
    certifiedValue: number | ''; setCertifiedValue: (v: number | '') => void;
    comments: string; setComments: (v: string) => void;
    owners: InventoryOwner[];
    removeOwner: (i: number) => void;
    ownerSearch: string; setOwnerSearch: (v: string) => void;
    ownerResults: any[]; isSearchingOwner: boolean; showOwnerDropdown: boolean;
    searchOwner: (query: string) => void; handleSelectOwner: (person: any) => void;
}

const InventoryForm = ({
    errorFields,
    name, setName,
    description, setDescription,
    inventoryType, setInventoryType,
    legalRegime, setLegalRegime,
    quantityDescription, setQuantityDescription,
    custodyAddress, setCustodyAddress,
    custodyCity, setCustodyCity,
    custodyState, setCustodyState,
    registrationNumber, setRegistrationNumber,
    registryOffice, setRegistryOffice,
    registryState, setRegistryState,
    trusteeName, setTrusteeName,
    trusteeDocument, setTrusteeDocument,
    certificateId, setCertificateId,
    certificateDate, setCertificateDate,
    certifiedValue, setCertifiedValue,
    comments, setComments,
    owners, removeOwner,
    ownerSearch, setOwnerSearch,
    ownerResults, isSearchingOwner, showOwnerDropdown,
    searchOwner, handleSelectOwner,
}: InventoryFormProps) => {
    return (
        <div className="p-4 space-y-6 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100">
            {/* Dados do Estoque */}
            <section>
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Dados do Estoque</h4>
                <div className="grid grid-cols-3 gap-3">
                    <SelectTextField label="Tipo *" id="inv-inventory-type" value={inventoryType}
                        onChange={e => setInventoryType(e.target.value as InventoryTypeValue | '')}
                        items={INVENTORY_TYPE_OPTIONS} emptyLabel="Selecione o tipo"
                        errorMessage={getErrorMessage(errorFields, 'inventoryType')} />
                    <TextEdit label="Nome / Identificação" id="inv-name" value={name}
                        onChange={e => setName(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'name')} />
                    <SelectTextField label="Regime Jurídico" id="inv-legal-regime" value={legalRegime}
                        onChange={e => setLegalRegime(e.target.value as LegalRegimeValue | '')}
                        items={LEGAL_REGIME_OPTIONS} emptyLabel="Selecione"
                        errorMessage={getErrorMessage(errorFields, 'legalRegime')} />
                </div>
                <div className="grid grid-cols-1 gap-3 mt-3">
                    <TextEdit label="Descrição *" id="inv-description" value={description}
                        onChange={e => setDescription(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'description')} />
                    <TextEdit label="Quantidade (multi-unidade: KG, L, UN)" id="inv-quantity" value={quantityDescription}
                        onChange={e => setQuantityDescription(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'quantityDescription')} />
                </div>
            </section>

            {/* Depósito */}
            <section>
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Depósito</h4>
                <div className="grid grid-cols-3 gap-3">
                    <TextEdit label="Endereço" id="inv-custody-address" value={custodyAddress}
                        onChange={e => setCustodyAddress(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'custodyAddress')} />
                    <TextEdit label="Cidade" id="inv-custody-city" value={custodyCity}
                        onChange={e => setCustodyCity(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'custodyCity')} />
                    <SelectTextField label="UF" id="inv-custody-state" value={custodyState}
                        onChange={e => setCustodyState(e.target.value)}
                        items={UF_OPTIONS} emptyLabel="Selecione"
                        errorMessage={getErrorMessage(errorFields, 'custodyState')} />
                </div>
                <div className="grid grid-cols-3 gap-3 mt-3">
                    <TextEdit label="Matrícula(s) do depósito" id="inv-registration-number" value={registrationNumber}
                        onChange={e => setRegistrationNumber(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'registrationNumber')} />
                    <TextEdit label="Cartório (CRI/RTD)" id="inv-registry-office" value={registryOffice}
                        onChange={e => setRegistryOffice(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'registryOffice')} />
                    <SelectTextField label="UF Cartório" id="inv-registry-state" value={registryState}
                        onChange={e => setRegistryState(e.target.value)}
                        items={UF_OPTIONS} emptyLabel="Selecione"
                        errorMessage={getErrorMessage(errorFields, 'registryState')} />
                </div>
            </section>

            {/* Fiel depositário e certificado */}
            <section>
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Fiel Depositário e Certificado</h4>
                <div className="grid grid-cols-2 gap-3">
                    <TextEdit label="Fiel depositário" id="inv-trustee-name" value={trusteeName}
                        onChange={e => setTrusteeName(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'trusteeName')} />
                    <TextEdit label="CPF/CNPJ do fiel depositário" id="inv-trustee-document" value={trusteeDocument}
                        onChange={e => setTrusteeDocument(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'trusteeDocument')} />
                </div>
                <div className="grid grid-cols-3 gap-3 mt-3">
                    <TextEdit label="ID do certificado" id="inv-certificate-id" value={certificateId}
                        onChange={e => setCertificateId(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'certificateId')} />
                    <TextEdit label="Data do certificado" id="inv-certificate-date" type="date" value={certificateDate}
                        onChange={e => setCertificateDate(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'certificateDate')} />
                    <CurrencyInput label="Valor certificado" id="inv-certified-value" prefix="R$" fractionDigits={2}
                        value={certifiedValue === '' ? 0 : certifiedValue}
                        onChange={v => setCertifiedValue(v)} />
                </div>
            </section>

            {/* Proprietários */}
            <section>
                <div className="flex items-center justify-between mb-3">
                    <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Proprietários *</h4>
                    <span className="text-xs text-gray-400">{owners.length} / {MAX_OWNERS}</span>
                </div>
                <div className="relative">
                    <TextEdit label="Nome / Razão Social (Enter para buscar)" id="inv-owner-search" value={ownerSearch}
                        onChange={e => setOwnerSearch(e.target.value)}
                        onKeyDown={(e: any) => e.key === 'Enter' && searchOwner(ownerSearch)}
                        errorMessage={getErrorMessage(errorFields, 'owners')} />
                    {isSearchingOwner && <div className="text-xs text-gray-400 mt-1">Buscando...</div>}
                    {showOwnerDropdown && ownerResults.length > 0 && (
                        <div className="absolute z-20 w-full mt-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg shadow-lg max-h-48 overflow-auto">
                            {ownerResults.slice(0, 8).map((p: any, idx: number) => (
                                <button type="button" key={p.personId || idx} onClick={() => handleSelectOwner(p)}
                                    className="w-full text-left px-3 py-2 hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer text-sm">
                                    <span className="font-medium dark:text-white">{p.name}</span>
                                    {p.documentFormatted && <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">{p.documentFormatted}</span>}
                                    <span className="text-[10px] text-gray-400 ml-1">({p.type})</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                {owners.length > 0 && (
                    <div className="space-y-1 mt-3">
                        {owners.map((o, i) => (
                            <div key={o.personId || i} className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-slate-800 rounded text-xs border border-gray-100 dark:border-slate-700">
                                <div className="flex items-center gap-3">
                                    <span className="font-medium text-gray-700 dark:text-gray-300">{o.name}</span>
                                    {o.document && <span className="text-gray-500 dark:text-gray-400">{o.document}</span>}
                                </div>
                                <button type="button" onClick={() => removeOwner(i)}
                                    className="text-red-500 hover:text-red-700 text-xs font-medium">Remover</button>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* Comentários */}
            <section>
                <TextEdit label="Comentários" id="inv-comments" value={comments}
                    onChange={e => setComments(e.target.value)}
                    errorMessage={getErrorMessage(errorFields, 'comments')} />
            </section>
        </div>
    );
};

export default InventoryForm;
